/** 仕様書 §5 に対応するドメイン型定義。 */

export const SCHEMA_VERSION = 1;

/** ISO8601（UTC）。例: "2026-08-26T05:30:12.345Z" */
export type IsoUtc = string;
/** ISO8601（端末ローカル時刻＋オフセット）。例: "2026-08-26T14:30:00+09:00" */
export type IsoLocal = string;
/** "YYYY-MM"。記事の保存先フォルダを決めるキー。 */
export type MonthKey = string;

export type LocationSource = 'exif' | 'device';

export interface EntryLocation {
  lat: number;
  lng: number;
  /** メートル。EXIF由来の場合は null。 */
  accuracy: number | null;
  source: LocationSource;
  capturedAt: IsoUtc;
}

export type MediaType = 'photo' | 'drawing';

export interface MediaRef {
  id: string;
  type: MediaType;
  /** media/ 配下のファイル名。 */
  file: string;
  mime: string;
  width: number;
  height: number;
  bytes: number;
  /** 手書きの場合のみ。drawings/ 配下のストロークJSON。 */
  strokesFile?: string;
  addedAt: IsoUtc;
}

export interface Entry {
  schemaVersion: number;
  id: string;
  /** 記事の日付。表示・並び順・保存フォルダの決定に使う。 */
  date: IsoLocal;
  createdAt: IsoUtc;
  updatedAt: IsoUtc;
  rev: number;
  deviceId: string;
  categoryId: string | null;
  /** 所属プロジェクト。設定されている場合、一覧ではカテゴリの代わりに表示する。 */
  projectId?: string | null;
  title: string;
  body: string;
  location: EntryLocation | null;
  media: MediaRef[];
}

export interface Category {
  id: string;
  name: string;
  /** 16進カラー。例: "#4A90D9" */
  color: string;
  icon: string;
  order: number;
  /** 論理削除。記事側の categoryId は書き換えない（仕様書 §5.3）。 */
  deleted: boolean;
}

export interface CategoriesDoc {
  schemaVersion: number;
  updatedAt: IsoUtc;
  categories: Category[];
}

export interface SettingsDoc {
  schemaVersion: number;
  updatedAt: IsoUtc;
  defaultCategoryId: string | null;
  appVersion: string;
}

export interface Tombstone {
  entryId: string;
  /** 削除時点で記事が置かれていた月。 */
  entryMonth: MonthKey;
  deletedAt: IsoUtc;
  deviceId: string;
}

/** 手書きストローク（仕様書 §5.7）。points は [x, y, pressure, tMs]。 */
export type StrokePoint = [number, number, number, number];

export interface Stroke {
  tool: 'pen' | 'eraser';
  color: string;
  size: number;
  points: StrokePoint[];
}

export interface StrokeDoc {
  schemaVersion: number;
  width: number;
  height: number;
  background: string;
  strokes: Stroke[];
}

export const DRAWING_WIDTH = 1024;
export const DRAWING_HEIGHT = 1448;
export const PHOTO_MAX_EDGE = 1024;
export const PHOTO_JPEG_QUALITY = 0.85;

/* ---------------- 目標・ゴール（期間 × 領域） ---------------- */

export type Horizon = '1y' | '5y' | '10y';

export const HORIZONS: Horizon[] = ['1y', '5y', '10y'];

export const HORIZON_LABELS: Record<Horizon, string> = {
  '1y': '1年後',
  '5y': '5年後',
  '10y': '10年後',
};

export interface GoalArea {
  id: string;
  name: string;
  order: number;
  /** 論理削除。過去に書いた内容は残す。 */
  deleted: boolean;
}

export interface GoalCell {
  text: string;
  updatedAt: IsoUtc;
}

export interface GoalsDoc {
  schemaVersion: number;
  updatedAt: IsoUtc;
  deviceId: string;
  areas: GoalArea[];
  /** キーは `${horizon}:${areaId}`。 */
  cells: Record<string, GoalCell>;
}

export const DEFAULT_GOAL_AREAS: GoalArea[] = [
  { id: 'area-work', name: '仕事', order: 1, deleted: false },
  { id: 'area-private', name: 'プライベート', order: 2, deleted: false },
  { id: 'area-money', name: '経済', order: 3, deleted: false },
  { id: 'area-mind', name: '心', order: 4, deleted: false },
  { id: 'area-people', name: '人脈', order: 5, deleted: false },
];

export function cellKey(h: Horizon, areaId: string): string {
  return `${h}:${areaId}`;
}

/* ---------------- 一日のまとめ ---------------- */

export interface DailySummary {
  schemaVersion: number;
  /** "YYYY-MM-DD"。1日につき1件。 */
  date: string;
  createdAt: IsoUtc;
  updatedAt: IsoUtc;
  rev: number;
  deviceId: string;
  /** 今日の主な出来事 */
  events: string;
  /** よかったこと */
  good: string;
  /** 明日試すワンアクション */
  action: string;
}

export function isDailyEmpty(d: Pick<DailySummary, 'events' | 'good' | 'action'>): boolean {
  return d.events.trim() === '' && d.good.trim() === '' && d.action.trim() === '';
}

/* ---------------- 週次レビュー（AI） ---------------- */

export interface WeeklyReview {
  schemaVersion: number;
  /** "YYYY-Www"（ISO週）。 */
  week: string;
  from: string;
  to: string;
  generatedAt: IsoUtc;
  model: string;
  /** 週全体の要約。 */
  summary: string;
  /** ゴールに沿って行動できたこと。 */
  aligned: string[];
  /** 改善したほうがよいこと。 */
  improve: string[];
  /** 来週試す具体的な一手。 */
  nextAction: string;
}

/* ---------------- 繰り返しタスク ---------------- */

export type TaskKind = 'daily' | 'weekly';
export type TaskValueType = 'number' | 'check' | 'text';
/** 数値タスクを期間で集計する方法。 */
export type TaskAggregate = 'sum' | 'avg' | 'last';

export interface TaskDef {
  id: string;
  kind: TaskKind;
  name: string;
  type: TaskValueType;
  /** 数値タスクの単位（kg、円、回 など）。 */
  unit?: string;
  /** 数値タスクの集計方法。既定は 'sum'。体重などは 'avg' が適切。 */
  aggregate?: TaskAggregate;
  /**
   * 目標値。
   * number: 1回あたりの目標。check: 期間内に達成したい回数の割合ではなく回数。
   * null なら目標なし。
   */
  target?: number | null;
  order: number;
  /** 停止。過去の記録は残したまま、入力欄から外す。 */
  archived: boolean;
  /** これ以前の期間は集計対象にしない（後から追加したタスクを未達扱いにしないため）。 */
  createdAt: IsoUtc;
}

export interface TasksDoc {
  schemaVersion: number;
  updatedAt: IsoUtc;
  deviceId: string;
  tasks: TaskDef[];
}

export type TaskValue = number | boolean | string;

/** ある1日（または1週）のタスク入力値。入力があったぶんだけ保存する。 */
export interface TaskEntry {
  schemaVersion: number;
  kind: TaskKind;
  /** daily は "YYYY-MM-DD"、weekly は "YYYY-Www"。 */
  period: string;
  createdAt: IsoUtc;
  updatedAt: IsoUtc;
  rev: number;
  deviceId: string;
  /** taskId → 値。空文字・未入力のキーは持たない。 */
  values: Record<string, TaskValue>;
}

export function taskEntryKey(kind: TaskKind, period: string): string {
  return `${kind === 'daily' ? 'd' : 'w'}:${period}`;
}

export function isTaskValueEmpty(v: TaskValue | undefined): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'boolean') return v === false;
  return Number.isNaN(v);
}

export const TASK_TYPE_LABELS: Record<TaskValueType, string> = {
  number: '数値',
  check: 'チェック',
  text: 'テキスト',
};

export const TASK_AGGREGATE_LABELS: Record<TaskAggregate, string> = {
  sum: '合計',
  avg: '平均',
  last: '最新値',
};

/* ---------------- プロジェクト ---------------- */

export type ProjectStatus = 'active' | 'onhold' | 'done';

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  active: '進行中',
  onhold: '保留',
  done: '完了',
};

export interface Milestone {
  id: string;
  title: string;
  /** "YYYY-MM-DD"。未設定なら null。 */
  due: string | null;
  done: boolean;
  doneAt: IsoUtc | null;
  order: number;
}

export interface Project {
  id: string;
  name: string;
  color: string;
  description: string;
  status: ProjectStatus;
  /** 締め切り "YYYY-MM-DD"。未設定なら null。 */
  deadline: string | null;
  /** マイルストーンが無いときに使う手動の進捗（0〜100）。 */
  manualProgress: number;
  milestones: Milestone[];
  createdAt: IsoUtc;
  updatedAt: IsoUtc;
  archived: boolean;
  /**
   * 削除日時。物理削除にすると、同期のマージでリモート側から復活してしまうため
   * 論理削除にしている（カテゴリ・ゴールの領域と同じ方針）。
   */
  deletedAt?: IsoUtc | null;
}

export interface ProjectsDoc {
  schemaVersion: number;
  updatedAt: IsoUtc;
  deviceId: string;
  projects: Project[];
}

/**
 * 進捗率。マイルストーンがあれば完了率から算出し、無ければ手動値を使う。
 * 数字を二重に持たせると、どちらが正か分からなくなるため一本化している。
 */
export function projectProgress(p: Project): { percent: number; derived: boolean } {
  if (p.status === 'done') return { percent: 100, derived: p.milestones.length > 0 };
  if (p.milestones.length === 0) {
    return { percent: Math.min(100, Math.max(0, p.manualProgress)), derived: false };
  }
  const done = p.milestones.filter((m) => m.done).length;
  return { percent: Math.round((done / p.milestones.length) * 100), derived: true };
}

/** 締め切りまでの日数。過ぎていれば負。締め切り未設定なら null。 */
export function daysUntil(dateKey: string | null, today: Date): number | null {
  if (!dateKey) return null;
  const [y, m, d] = dateKey.split('-').map(Number);
  const target = new Date(y, m - 1, d).getTime();
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.round((target - base) / 86400000);
}
